import { useEffect, useState } from 'react'
import { useBoolean } from 'react-use'
import { gql } from '@apollo/client'
import { useSubscription as useSubscriptionCore } from '@apollo/client/react'
import { onSocketClosed } from '@ztechapps/common.utils'

const DB_RESTORE_OUTPUT_SUBSCRIPTION = gql`
  subscription dbRestoreOutput {
    dbRestoreOutput {
      line
    }
  }
`

type DbRestoreOutputData = { dbRestoreOutput: { line: string } }

export type UseDbRestoreOutputResult = {
  /** The redacted mongorestore lines received since the hook mounted, oldest first. */
  lines: string[]
  /** The socket dropped, errored or closed: no more lines are appended, and nothing is replayed. */
  unavailable: boolean
}

/**
 * Live mongorestore output of a running database restore. It subscribes from mount, so mount it before the restore's
 * output starts. A socket drop, error or close only sets `unavailable` — it is handled here, never by the HTTP error
 * link — and ends the subscription for good, so a reconnect cannot resume a log that has a gap in it.
 */
export const useDbRestoreOutput = (): UseDbRestoreOutputResult => {
  const [lines, setLines] = useState<string[]>([])
  const [unavailable, setUnavailable] = useBoolean(false)

  // graphql-ws retries a dropped socket and re-sends the subscription on its own, so neither `onError` nor
  // `onComplete` fires for a plain drop; the app's graphql-ws client announces it through `notifySocketClosed`.
  // `useBoolean`'s setter flips on a non-boolean argument, so it is never handed a listener argument.
  useEffect(() => onSocketClosed(() => setUnavailable(true)), [setUnavailable])

  useSubscriptionCore<DbRestoreOutputData>(DB_RESTORE_OUTPUT_SUBSCRIPTION, {
    // Skipping releases the subscription, so lines the server sends after a reconnect are not appended.
    skip: unavailable,
    onData: ({ data }) => {
      const line = data.data?.dbRestoreOutput?.line
      if (line) setLines((previous) => [...previous, line])
    },
    onError: () => setUnavailable(true),
    onComplete: () => setUnavailable(true),
  })

  return { lines, unavailable }
}
